import { useEffect, useRef } from 'react';
import { useAudio } from '../contexts/AudioContext';

/**
 * AudioPlayer component that handles audio playback and manages audio state.
 * 
 * @param {string} url - The URL of the audio file to play.
 * @param {function} onEndedAudio - Callback function to call when the audio ends.
 * @returns {JSX.Element} The rendered audio player component.
 */
export function AudioPlayer({ url, onEndedAudio }) {
    const { playingAudio, setPlayingAudio } = useAudio();
    const audioRef = useRef(null);

    /**
     * Pauses the playing audio, if there is one, and calls the function to play the new audio
     */
    const playSound = () => {
        // If there is an audio playing already, pause it first
        if (playingAudio) {
            playingAudio.pause();
            playingAudio.onpause = () => {
                playNewSound();
            }
        }
        else {
            playNewSound();
        }
    };

    /**
     * Plays the new audio.
     */
    const playNewSound = () => {
        const newAudio = new Audio(url);
        audioRef.current = newAudio;

        const audioPromise = newAudio.play();
        if (audioPromise !== undefined) {
            audioPromise.then(() => {
                console.log("Audio played auto");

                // Set audio in global context
                setPlayingAudio(newAudio);
            }).catch(error => {
                console.error("Failed to play audio:", error);
            });
        }

        // When the audio has ended, clear the global context and call the callback function
        newAudio.onended = () => {
            setPlayingAudio(null);
            if (onEndedAudio) onEndedAudio();
        };
    }

    /**
     * Play audio when the URL changes
     */
    useEffect(() => {
        if (url) {
            playSound();
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                setPlayingAudio(null);
            }
        };
    }, [url]);
}